import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

export interface FooterConfig {
  showFooter?: boolean;
  showBrand?: boolean;
  showQuickLinks?: boolean;
  showSupport?: boolean;
  showSocial?: boolean;
  showBottomSection?: boolean;
  backgroundColor?: string;
  textColor?: string;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  template: `
    <footer
      [class]="
        effectiveConfig.backgroundColor + ' ' + effectiveConfig.textColor
      "
      *ngIf="effectiveConfig.showFooter"
    >
      <div class="mx-auto w-full max-w-screen-xl p-4 py-6 lg:py-8">
        <div class="md:flex md:justify-between">
          <!-- Brand Section -->
          <div class="mb-6 md:mb-0" *ngIf="effectiveConfig.showBrand">
            <a routerLink="/offers" class="flex items-center">
              <div
                class="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3"
              >
                <span class="text-white font-bold text-lg">G</span>
              </div>
              <span class="self-center text-2xl font-semibold whitespace-nowrap"
                >Gastronauta</span
              >
            </a>
            <p class="mt-4 max-w-md text-gray-500 sm:text-sm">
              {{ 'footer.description' | translate }}
            </p>
            <!-- Social Media -->
            <div
              class="flex mt-4 space-x-5 sm:mt-6"
              *ngIf="effectiveConfig.showSocial"
            >
              <a href="#" class="text-gray-500 hover:text-gray-900">
                <svg
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                <span class="sr-only">Facebook page</span>
              </a>
              <a href="#" class="text-gray-500 hover:text-gray-900">
                <svg
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"
                  ></path>
                </svg>
                <span class="sr-only">Twitter page</span>
              </a>
              <a href="#" class="text-gray-500 hover:text-gray-900">
                <svg
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.012.001z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                <span class="sr-only">Pinterest page</span>
              </a>
              <a href="#" class="text-gray-500 hover:text-gray-900">
                <svg
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.012.001z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                <span class="sr-only">Instagram page</span>
              </a>
            </div>
          </div>

          <!-- Quick Links -->
          <div
            class="grid grid-cols-2 gap-8 sm:gap-6 sm:grid-cols-3"
            *ngIf="
              effectiveConfig.showQuickLinks || effectiveConfig.showSupport
            "
          >
            <!-- Quick Links -->
            <div *ngIf="effectiveConfig.showQuickLinks">
              <h2 class="mb-6 text-sm font-semibold text-gray-900 uppercase">
                {{ 'footer.quickLinks' | translate }}
              </h2>
              <ul class="text-gray-500 font-medium">
                <li class="mb-4">
                  <a routerLink="/offers" class="hover:underline">{{ 'nav.offers' | translate }}</a>
                </li>
                <li class="mb-4">
                  <a routerLink="/menus" class="hover:underline">{{ 'nav.menus' | translate }}</a>
                </li>
                <li class="mb-4">
                  <a routerLink="/places" class="hover:underline">Lugares</a>
                </li>
                <li class="mb-4">
                  <a routerLink="/liked-offers" class="hover:underline"
                    >{{ 'nav.favorites' | translate }}</a
                  >
                </li>
              </ul>
            </div>

            <!-- Support -->
            <div *ngIf="effectiveConfig.showSupport">
              <h2 class="mb-6 text-sm font-semibold text-gray-900 uppercase">
                Soporte
              </h2>
              <ul class="text-gray-500 font-medium">
                <li class="mb-4">
                  <a routerLink="/settings" class="hover:underline"
                    >{{ 'nav.settings' | translate }}</a
                  >
                </li>
                <li class="mb-4">
                  <a href="#" class="hover:underline">Ayuda</a>
                </li>
                <li class="mb-4">
                  <a href="#" class="hover:underline">Contacto</a>
                </li>
                <li class="mb-4">
                  <a href="#" class="hover:underline">Términos de Uso</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Bottom Section -->
        <hr
          class="my-6 border-gray-200 sm:mx-auto lg:my-8"
          *ngIf="effectiveConfig.showBottomSection"
        />
        <div
          class="sm:flex sm:items-center sm:justify-between"
          *ngIf="effectiveConfig.showBottomSection"
        >
          <span class="text-sm text-gray-500 sm:text-center">
            © 2024
            <a routerLink="/offers" class="hover:underline">Gastronauta</a>.
            Todos los derechos reservados.
          </span>
          <div class="flex mt-4 space-x-5 sm:justify-center sm:mt-0">
            <a href="#" class="text-gray-500 hover:text-gray-900">
              <span class="sr-only">{{ 'consent.privacyPolicy' | translate }}</span>
              {{ 'consent.privacyPolicy' | translate }}
            </a>
            <a href="#" class="text-gray-500 hover:text-gray-900">
              <span class="sr-only">Cookies</span>
              Cookies
            </a>
            <a href="#" class="text-gray-500 hover:text-gray-900">
              <span class="sr-only">Legal</span>
              Legal
            </a>
          </div>
        </div>
      </div>
    </footer>
  `,
  styles: [
    `
      /* Host element must be block for flex layout */
      :host {
        display: block;
        flex-shrink: 0;
      }
    `,
  ],
})
export class FooterComponent {
  @Input() config: FooterConfig = {};

  // Default configuration
  private defaultConfig: FooterConfig = {
    showFooter: true,
    showBrand: true,
    showQuickLinks: true,
    showSupport: true,
    showSocial: true,
    showBottomSection: true,
    backgroundColor: 'bg-gray-900',
    textColor: 'text-white',
  };

  get effectiveConfig(): FooterConfig {
    return { ...this.defaultConfig, ...this.config };
  }

  constructor() {
    console.log('FooterComponent initialized');
  }
}
